import { useSearchParams } from "@solidjs/router";
import { Suspense } from "solid-js";
import { CompanyLayoutSkeleton } from "@/components/layout/CompanyLayout";
import { EligibilityGuard } from "@/domains/billing/components/EligibilityGuard";
import { consumeRecentSearchRerun } from "@/domains/company/recent-searches/store/rerun-intent";
import CompanySearchFreelancerResultRoute from "./route";

/**
 * `/result` behind the `offer-creation` quota guard, with its two exemptions (AC 10, AC 10b).
 *
 * **Why it is a module of its own** and not an inline component inside the routes array of
 * `AppRouter.tsx`, where it used to live: the review found that four separate mutations of this
 * logic — inverting the `!!`, turning the `||` into `&&`, dropping the guard, moving the
 * `consumeRecentSearchRerun()` call inside `skip` — left the whole suite, the typecheck and biome
 * green, because nothing imported `AppRouter.tsx`. A quota bypass with no test at all was the
 * second BLOCKER of that round. Here the rule is a unit, and `guarded-route.test.tsx` is its net.
 * The routes array keeps a plain reference; nothing else changed about the routing.
 *
 * The rule itself:
 *
 * - `/result` is shared by both search flows and runs the search straight from URL params (no
 *   `enabled` gate). A from-scratch search (no `job_offer_id`) creates a new offer, so it must
 *   pass the offer-creation quota guard — this closes the deep-link/bookmark bypass that
 *   `/search`'s guard alone could not (a sibling leaf, not a child).
 * - The from-offer flow carries `job_offer_id`, reuses an existing offer, and stays exempt (D6).
 * - A rerun from «Le tue ricerche recenti» is exempt too (AC 10): every history entry is
 *   rerunnable, quota or not, because the search itself creates nothing — the offer is born at
 *   the proposal, where the backend still answers `403 plan_limit_exceeded` and
 *   `PlanLimitWatcher` opens the paywall.
 *
 * **Why an in-memory flag and not `navigate(path, { state })`** (P1): Solid Router 0.16 keeps
 * navigation state in `history.state`, which the browser persists across a reload — the exemption
 * would stick to that history entry forever and reloading the results URL would skip the guard,
 * exactly the bypass AC 10b forbids. A module flag dies with the JS realm, so a reload, a copied
 * URL, a bookmark and a new tab all land back under the guard. Declared cost: browser back →
 * forward onto a rerun's results page goes through the guard, because a forward press is not "a
 * navigation started from the row".
 *
 * `consumeRecentSearchRerun()` is **destructive** and is read once here, in the component body —
 * never inside `skip={…}` or any other reactive position, where the first recomputation would
 * read `false` and drop the exemption mid-flight. In Solid a `skip={expr}` prop is a getter that
 * re-runs `expr` on **every** read, so moving the call in there would consume the flag on the
 * first read and hand `false` to the second.
 */
export function CompanySearchFreelancerResultGuardedRoute() {
  const [searchParams] = useSearchParams();
  const fromRecentSearch = consumeRecentSearchRerun();

  return (
    <EligibilityGuard
      action="offer-creation"
      skip={!!searchParams.job_offer_id || fromRecentSearch}
    >
      <Suspense fallback={<CompanyLayoutSkeleton />}>
        <CompanySearchFreelancerResultRoute />
      </Suspense>
    </EligibilityGuard>
  );
}

export default CompanySearchFreelancerResultGuardedRoute;
